import React from 'react';

import { LayoutRenderer, RowCollectionLayout } from './types';
import { buildTilePlaceholder, ensureTileCount, sanitizeLayoutClass } from './utils';

const createGridStubLayout = (layoutType: RowCollectionLayout, slots: number): LayoutRenderer => {
  const GridLayout: LayoutRenderer = ({ children, placeholder }) => {
    const nodes = React.Children.toArray(children).filter(Boolean).slice(0, slots);
    const tiles = ensureTileCount(nodes, slots, (index) =>
      buildTilePlaceholder(placeholder, `${layoutType}-placeholder-${index}`)
    );
    const className = `itv-row-collection__grid itv-row-collection__grid--${sanitizeLayoutClass(layoutType)}`;

    return (
      <div className={className}>
        {tiles.map((tile, index) => (
          <div key={`${layoutType}-grid-cell-${index}`} className="itv-row-collection__grid-cell">
            {tile}
          </div>
        ))}
      </div>
    );
  };

  GridLayout.displayName = `GridStubLayout(${layoutType})`;

  return GridLayout;
};

export const RowThreeLayout = createGridStubLayout('row-3', 3);
export const RowFiveLayout = createGridStubLayout('row-5', 5);
export const RowSixLayout = createGridStubLayout('row-6', 6);
export const GridSixLayout = createGridStubLayout('grid-6', 6);

const TALL_GROUP_SIZE = 5;
const TALL_GRID_SIZE = 4;
const TALL_GROUP_COUNT = 2;

const GRID_TWO_BY_TWO_GROUP_SIZE = 4;
const GRID_TWO_BY_TWO_GROUP_COUNT = 2;

export const GridTwoByTwoLayout: LayoutRenderer = ({ children, placeholder }) => {
  const nodes = React.Children.toArray(children).filter(Boolean);
  const groups =
    nodes.length > 0
      ? Array.from({ length: GRID_TWO_BY_TWO_GROUP_COUNT }, (_, groupIndex) =>
          nodes.slice(groupIndex * GRID_TWO_BY_TWO_GROUP_SIZE, (groupIndex + 1) * GRID_TWO_BY_TWO_GROUP_SIZE)
        )
      : [[], []];

  return (
    <div className="itv-row-collection__double-grid">
      {groups.map((groupNodes, groupIndex) => {
        const tiles = ensureTileCount(groupNodes, GRID_TWO_BY_TWO_GROUP_SIZE, (index) =>
          buildTilePlaceholder(placeholder, `double-grid-2x2-${groupIndex}-${index}`)
        );

        return (
          <div className="itv-row-collection__double-grid-group" key={`double-grid-group-${groupIndex}`}>
            {tiles.map((tile, index) => (
              <div key={`double-grid-cell-${groupIndex}-${index}`} className="itv-row-collection__double-grid-cell">
                {tile}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

export const TallGridTwoByTwoLayout: LayoutRenderer = ({ children, placeholder }) => {
  const nodes = React.Children.toArray(children).filter(Boolean);
  const groups = Array.from({ length: TALL_GROUP_COUNT }, (_, groupIndex) => {
    const start = groupIndex * TALL_GROUP_SIZE;

    return nodes.slice(start, start + TALL_GROUP_SIZE);
  });

  return (
    <div className="itv-row-collection__tall-grid">
      {groups.map((groupNodes, groupIndex) => {
        const [hero, ...rest] = groupNodes;
        const heroTile = hero ?? buildTilePlaceholder(placeholder, `tall-grid-primary-${groupIndex}`, 'tall');
        const secondaryTiles = ensureTileCount(rest.slice(0, TALL_GRID_SIZE), TALL_GRID_SIZE, (index) =>
          buildTilePlaceholder(placeholder, `tall-grid-secondary-${groupIndex}-${index}`)
        );

        return (
          <div className="itv-row-collection__tall-grid-group" key={`tall-grid-group-${groupIndex}`}>
            <div className="itv-row-collection__tall-grid-primary">{heroTile}</div>
            <div className="itv-row-collection__tall-grid-secondary">
              {secondaryTiles.map((tile, index) => (
                <div
                  key={`tall-grid-secondary-cell-${groupIndex}-${index}`}
                  className="itv-row-collection__tall-grid-cell"
                >
                  {tile}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
